import createIntlWithLocale from 'src/internal/createIntlWithLocale';

import type {IAnalytics, IReports} from '@sail/observability';
import type {MessageDescriptor} from 'src/public/message/types';
import type {IntlConfig} from 'src/types';

/**
 * @example Basic {{include "./examples/isTranslated.basic.tsx"}}
 *
 * @see https://sail.stripe.me/apis/intl/useIntl/#is-translated
 */
export default function isTranslated<
  /** Generic type for the provided message descriptor map object  */
  TMessage extends MessageDescriptor<unknown>,
>(
  /** The intl config object containing the locale data. When using this API through the `useIntl` hook, this config object is automatically injected. */
  config: IntlConfig,
  /** useReports() return value. When using this API through the `useIntl` hook, this config object is automatically injected. */
  reports: IReports,
  /** useAnalytics() return value. When using this API through the `useIntl` hook, this config object is automatically injected. */
  analytics: IAnalytics,
  /** The message descriptor map object passed-in to check against translations.
   * This is the only argument that needs to be passed.
   * The other 3 are automatically injected when using useIntl(). */
  descriptor: TMessage,
): boolean {
  const locale = config.locale;
  const language: string = locale.split('-')[0];
  const intl = createIntlWithLocale(locale, config, reports, analytics);

  if (language === 'en') {
    return true;
  }

  if (!intl.messages) {
    return false;
  }

  return descriptor.id in intl.messages;
}

export type IsTranslated = typeof isTranslated;

export type IsTranslatedDeclarative = <
  TMessage extends MessageDescriptor<unknown>,
>(
  descriptor: TMessage,
) => boolean;
